/**
 * A run being played: the game, the current state and the command log. The
 * only thing it does is send commands to game-core and keep what came back.
 *
 * Two ways a floor's seed arrives (ADR 0002, 0034):
 * - **`run` seeds** (a local run: the terminal client, `?run=1`, the golden
 *   logs): floors are entered automatically with seeds from the run seed:
 *   floor 1 gets `seed`, floor 2 `seed + 1`, and so on (mod 2^32).
 * - **`server` seeds** (a run the server verifies): the session stops between
 *   floors; the client submits the floor's log (`floorLog`, `stateHash`) and
 *   enters the next floor with the seed the server answers (`enterFloor`). A
 *   whole-run seed never exists. `restoreSession` rebuilds such a run from the
 *   logs of its verified floors (the mid-run save: `GET /api/runs/current`).
 */
import {
  type ApplyResult,
  type BagEntry,
  type Command,
  type Event,
  type Game,
  type PartyMember,
  type RunState,
  InvalidCommand,
  type RefusalArgs,
  type RefusalCode,
} from '@ab/game-core';
import { type GoldenLog, stateHash } from './golden.ts';

/** Where the next floor's seed comes from: the run's seed (`run`), or the server's answer to the last floor (`server`). */
export type SeedSource = 'run' | 'server';

export type Session = {
  readonly game: Game;
  readonly dungeon: string;
  readonly party: readonly PartyMember[];
  /** The stacks the run started with in its bag. */
  readonly bag: readonly BagEntry[];
  /** The seed of floor 1 (a `server` session's: the seed it entered floor 1 with). */
  readonly seed: number;
  readonly seeds: SeedSource;
  state: RunState;
  readonly commands: Command[];
};

export type SendResult =
  | { readonly ok: true; readonly events: readonly Event[]; readonly before: RunState }
  | { readonly ok: false; readonly message: string; readonly code: RefusalCode; readonly args: RefusalArgs };

/** The `enter_floor` seed for `floorNumber` (1-based) of a run started with `seed` (a `run`-seeded session's). */
export const floorSeed = (seed: number, floorNumber: number): number => (seed + floorNumber - 1) >>> 0;

/** Applies one command to the session, keeping it; an `InvalidCommand` leaves the session unchanged. */
const applyOne = (session: Session, command: Command): SendResult => {
  const before = session.state;
  let result: ApplyResult;
  try {
    result = session.game.apply(before, command);
  } catch (e) {
    if (e instanceof InvalidCommand) return { ok: false, message: e.message, code: e.code, args: e.args };
    throw e;
  }
  session.state = result.state;
  session.commands.push(command);
  return { ok: true, events: result.events, before };
};

/**
 * Applies one command. An `InvalidCommand` leaves the session unchanged and
 * returns its message, code and args (`describeRefusal` says it in a table's
 * language). After a command that leaves the floor, a `run`-seeded session
 * enters the next floor at once and appends its events; a `server`-seeded one
 * stays between floors until `enterFloor`.
 */
export const send = (session: Session, command: Command): SendResult => {
  const r = applyOne(session, command);
  if (!r.ok) return r;
  const events = [...r.events];
  if (session.state.phase === 'between_floors' && session.seeds === 'run') {
    const entered = applyOne(session, { type: 'enter_floor', seed: floorSeed(session.seed, session.state.floorNumber + 1) });
    if (!entered.ok) throw new Error(entered.message);
    events.push(...entered.events);
  }
  return { ok: true, events, before: r.before };
};

/** Enters the next floor with `seed` (a `server`-seeded session, with the seed the server answered). */
export const enterFloor = (session: Session, seed: number): SendResult => applyOne(session, { type: 'enter_floor', seed });

/** The commands the player sent: the log without the `enter_floor` commands the session sends itself. */
export const playerCommandCount = (session: Session): number => session.commands.filter((c) => c.type !== 'enter_floor').length;

/** The seed the floor on screen was entered with (the last `enter_floor`); null before floor 1. */
export const currentFloorSeed = (session: Session): number | null => {
  for (let i = session.commands.length - 1; i >= 0; i -= 1) {
    const c = session.commands[i];
    if (c?.type === 'enter_floor') return c.seed;
  }
  return null;
};

/**
 * The current floor's log, as the server verifies it (ADR 0002, 0034): every command from the floor's `enter_floor`
 * on (it included), up to the command that left the floor or ended the run.
 */
export const floorLog = (session: Session): Command[] => {
  let from = session.commands.length;
  while (from > 0 && session.commands[from - 1]?.type !== 'enter_floor') from -= 1;
  return session.commands.slice(Math.max(0, from - 1));
};

/** The floor is over: the state the server checks (between floors, or the run's end). */
export const floorOver = (session: Session): boolean => session.state.phase !== 'playing';

/**
 * A new run, already on floor 1. The bag starts with `bag`: by default the
 * new-player gift (`rules.newPlayer.starterBag`), which the real game delivers
 * once per account through the server's mailbox (roadmap step 3, later); until
 * then every run starts with it (the server's runs too, ADR 0034).
 */
export const startSession = (
  game: Game,
  dungeon: string,
  party: readonly PartyMember[],
  seed: number,
  bag: readonly BagEntry[] = game.content.rules.newPlayer.starterBag,
  seeds: SeedSource = 'run',
): { session: Session; events: readonly Event[] } => {
  const session: Session = { game, dungeon, party, bag, seed, seeds, state: game.createRun(dungeon, party, bag), commands: [] };
  const r = send(session, { type: 'enter_floor', seed: seeds === 'run' ? floorSeed(seed, 1) : seed });
  if (!r.ok) throw new Error(r.message);
  return { session, events: r.events };
};

/**
 * A `server`-seeded run rebuilt from the logs of its verified floors, then entered on the current floor with `seed`
 * (the mid-run save of ADR 0002: a reload or another device resumes at the start of the floor being played). Each
 * logged command is applied in order; one game-core refuses throws (the logs were verified with this build). `before`
 * is the state the current floor was entered from (the opening events' "before").
 */
export const restoreSession = (
  game: Game,
  run: { readonly dungeon: string; readonly party: readonly PartyMember[]; readonly bag: readonly BagEntry[]; readonly floors: readonly (readonly Command[])[]; readonly seed: number },
): { session: Session; events: readonly Event[]; before: RunState } => {
  const first = run.floors[0]?.[0];
  const seed = first?.type === 'enter_floor' ? first.seed : run.seed;
  const session: Session = { game, dungeon: run.dungeon, party: run.party, bag: run.bag, seed, seeds: 'server', state: game.createRun(run.dungeon, run.party, run.bag), commands: [] };
  for (const command of run.floors.flat()) {
    const r = applyOne(session, command);
    if (!r.ok) throw new Error(`a verified floor's command was refused: ${r.message}`);
  }
  const r = enterFloor(session, run.seed);
  if (!r.ok) throw new Error(r.message);
  return { session, events: r.events, before: r.before };
};

/** The session as a golden log (see packages/game-core/test/golden/README.md). */
export const toGoldenLog = async (session: Session): Promise<GoldenLog> => ({
  dungeon: session.dungeon,
  party: session.party,
  bag: session.bag,
  commands: session.commands,
  // Filled in by the author: the event types the run is meant to show (README in the golden folder).
  expects: [],
  hash: await stateHash(session.state),
});
